// A short memory of what the spoken agent did on this screen: sessions, what
// was heard and said, every tool call with its answer, and errors. Kept in
// localStorage so a problem can be read back after the fact, from Settings →
// Voice, without a developer console. The server logs the same turns from its
// side; this is the device's half, where the tools actually run.
import { create } from 'zustand'
import { api } from '@/runtime'

export interface VoiceLogEntry {
  at: string
  kind: 'session' | 'heard' | 'said' | 'tool' | 'result' | 'error'
  text: string
}

const KEY = 'desk_voice_log'
const MAX = 80

const read = (): VoiceLogEntry[] => {
  try {
    const raw = localStorage.getItem(KEY)
    const j = raw ? (JSON.parse(raw) as VoiceLogEntry[]) : []
    return Array.isArray(j) ? j.slice(-MAX) : []
  } catch {
    return []
  }
}

interface VoiceLogState {
  entries: VoiceLogEntry[]
  add: (kind: VoiceLogEntry['kind'], text: string) => void
  clear: () => void
}

export const useVoiceLog = create<VoiceLogState>((set, get) => ({
  entries: read(),
  add: (kind, text) => {
    const entries = [
      ...get().entries,
      { at: new Date().toISOString(), kind, text: text.slice(0, 600) },
    ].slice(-MAX)
    set({ entries })
    try {
      localStorage.setItem(KEY, JSON.stringify(entries))
    } catch {
      /* private mode: the session still has it */
    }
  },
  clear: () => {
    set({ entries: [] })
    try {
      localStorage.removeItem(KEY)
    } catch {
      /* fine */
    }
  },
}))

export const voiceLog = (kind: VoiceLogEntry['kind'], text: string) => {
  useVoiceLog.getState().add(kind, text)
  // the server keeps the trail; the device's half of a tool call goes there too
  if (kind === 'tool' || kind === 'result' || kind === 'error' || kind === 'session') {
    void api
      .auditPost({
        level: kind === 'error' ? 'error' : 'info',
        area: 'voice',
        action:
          kind === 'tool'
            ? 'device_tool'
            : kind === 'result'
              ? 'device_result'
              : kind === 'error'
                ? 'device_error'
                : 'session',
        message: text.slice(0, 600),
      })
      .catch(() => {})
  }
}

/** This conversation's words, for the memory: what was heard and said since the last session start. */
export const sessionTranscript = (): { role: 'user' | 'assistant'; text: string }[] => {
  const entries = useVoiceLog.getState().entries
  let from = 0
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i]!.kind === 'session' && entries[i]!.text === 'start') {
      from = i
      break
    }
  }
  return entries
    .slice(from)
    .filter((e) => e.kind === 'heard' || e.kind === 'said')
    .map((e) => ({ role: e.kind === 'heard' ? ('user' as const) : ('assistant' as const), text: e.text }))
}

/** The log as text, for pasting into a bug report. */
export const voiceLogText = () =>
  useVoiceLog
    .getState()
    .entries.map((e) => `${e.at.slice(11, 19)} ${e.kind.padEnd(7)} ${e.text}`)
    .join('\n')
